import { useQueryClient } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import toast from "react-hot-toast";
import { supabase } from "../../lib/supabase";
import { queryKeys } from "../../lib/queryKeys";
import { NewSubtaskFields, SubTask, Task } from "../../types";

/**
 * Returns an `addSubtask(taskId, title, fields?)` that inserts a new subtask
 * under its parent task and appends it to the query cache — reflected
 * instantly in every consumer of `useTasks` (e.g. the open task dialog).
 * Server-first, mirroring {@link useCreateTask}: the row is inserted first,
 * then the row the database returns is written into the cache, so the
 * subtask carries its real id and `order_position` from the start. Optional
 * `fields` (description, schedule, priority, …) ride on the same insert so
 * email / voice drafts do not need a follow-up patch. On failure it captures
 * to Sentry, surfaces a toast, and returns `null`.
 *
 * This is the query-cache-aware replacement for the pre-migration addSubtask.
 */
export function useCreateSubtask() {
  const queryClient = useQueryClient();

  return async (
    taskId: string,
    title: string,
    fields?: NewSubtaskFields,
  ): Promise<SubTask | null> => {
    // Read the freshest task list at mutation time (not a stale render-time
    // snapshot) to compute the next order_position under this parent.
    const currentTasks =
      queryClient.getQueryData<Task[]>(queryKeys.tasks) ?? [];
    const parentTask = currentTasks.find((t) => t.id === taskId);
    const existingSubtasks = parentTask?.subtasks ?? [];
    const maxSubOrder =
      existingSubtasks.length > 0
        ? Math.max(...existingSubtasks.map((st) => st.order_position))
        : 0;

    const { data, error } = await supabase
      .from("subtasks")
      .insert({
        task_id: taskId,
        title,
        completed: false,
        order_position: maxSubOrder + 100,
        ...fields,
      })
      .select()
      .single();

    if (error || !data) {
      Sentry.captureException(
        error ?? new Error("Subtask insert returned no row"),
        { extra: { mutation: "useCreateSubtask", taskId } },
      );
      toast.error("Failed to add subtask. Please try again.");
      return null;
    }

    // Left alone when the cache has not loaded: writing `[]` into it would flip
    // useTasks out of its pending state and drop the app's loading gate, all to
    // publish a cache that does not contain the new subtask anyway. The
    // invalidation below brings the inserted row in.
    queryClient.setQueryData<Task[]>(queryKeys.tasks, (prev) =>
      prev
        ? prev.map((t) =>
            t.id === taskId
              ? { ...t, subtasks: [...(t.subtasks ?? []), data] }
              : t,
          )
        : prev,
    );

    queryClient.invalidateQueries({ queryKey: queryKeys.tasks });
    return data;
  };
}
